/* eslint-disable prefer-destructuring */
/* eslint-disable no-unused-vars */
import * as types from './types';
import * as accountTypes from '@/modules/account/store/types';

const getters = {
  [types.GET_CAMPAIGNS]: (state) => {
    if (!state.campaigns.length) {
      return [];
    }
    return state.campaigns;
  },
  [types.GET_ACCOUNT_ADVERTISER]: (state) => state.existingAdvertisers.find((advertiser) => advertiser.type === 'ACCOUNT'),
  [types.GET_COUNTRIES_LIST]: (state) => {
    if (state.countries === {}) {
      return [];
    }
    return Object.keys(state.countries).map((key) => ({
      value: key,
      text: state.countries[key],
    })).sort((a, b) => {
      if (a.text < b.text) { return -1; }
      if (a.text > b.text) { return 1; }
      return 0;
    });
  },
  [types.GET_ADVERTISER_LIST]: (state) => {
    if (state.existingAdvertisers === []) {
      return [];
    }
    return state.existingAdvertisers.map((advertiser) => ({
      value: advertiser.id,
      text: advertiser.name,
      accountId: advertiser.accountId,
    })).sort((a, b) => {
      if (a.text < b.text) { return -1; }
      if (a.text > b.text) { return 1; }
      return 0;
    });
  },
  [types.GET_SELECTED_CURRENCY]: (state) => {
    const defaultCurrency = {
      code: 'USD',
      id: 2,
      name: 'US Dollar',
      symbolPosition: 'PREFIX',
      symbolString: '$',
    };
    if (!state.currencies.length) return defaultCurrency;
    let selectedCurrencyId = defaultCurrency.id;
    if (state.orderChanges.hasOwnProperty('currencyId')) {
      selectedCurrencyId = state.orderChanges.currencyId;
    } else if (state.order.hasOwnProperty('currencyId')) {
      selectedCurrencyId = state.order.currencyId;
    }
    return state.currencies.find((currency) => currency.id === selectedCurrencyId);
  },
  [types.GET_CAMPAIGN_TOTAL_BUDGET]: (state) => {
    let periods = [];
    if (state.orderChanges.hasOwnProperty('periods')) {
      periods = state.orderChanges.periods;
    } else if (state.order.hasOwnProperty('periods')) {
      periods = state.order.periods;
    }
    if (periods.length) {
      return periods.map((period) => period.budget)
        .reduce((totalBudget, budget) => totalBudget + budget, 0);
    }
    if (state.order.hasOwnProperty('budget')) {
      return state.order.budget;
    }
    return null;
  },
  [types.GET_CAMPAIGN_TOTAL_IMPRESSION_TARGET]: (state) => {
    let periods = [];
    if (state.orderChanges.hasOwnProperty('periods')) {
      periods = state.orderChanges.periods;
    } else if (state.order.hasOwnProperty('periods')) {
      periods = state.order.periods;
    }
    const notNullImpressionTargets = periods.map((period) => period.impressionTarget)
      .filter((impressionTarget) => impressionTarget != null);
    return notNullImpressionTargets.length > 0
      ? notNullImpressionTargets.reduce((totalImpressionTarget, impressionTarget) => totalImpressionTarget + impressionTarget, 0) : null;
  },
  [types.GET_CAMPAIGN_MIN_START_DATE]: (state) => {
    let periods = [];
    if (state.orderChanges.hasOwnProperty('periods')) {
      periods = state.orderChanges.periods;
    } else if (state.order.hasOwnProperty('periods')) {
      periods = state.order.periods;
    }
    if (periods.length) {
      const notNullStartDates = periods.filter((period) => period.startDate);
      if (!notNullStartDates.length) return null;
      return notNullStartDates[0].startDate;
    }
    if (state.order.hasOwnProperty('startDate')) {
      return state.order.startDate;
    }
    return null;
  },
  [types.GET_CAMPAIGN_MAX_END_DATE]: (state) => {
    let periods = [];
    if (state.orderChanges.hasOwnProperty('periods')) {
      periods = state.orderChanges.periods;
    } else if (state.order.hasOwnProperty('periods')) {
      periods = state.order.periods;
    }
    if (periods.length) {
      const notNullEndDates = periods.filter((period) => period.endDate);
      if (!notNullEndDates.length) return null;
      return notNullEndDates[notNullEndDates.length - 1].endDate;
    }
    if (state.order.hasOwnProperty('endDate')) {
      return state.order.endDate;
    }
    return null;
  },
  [types.GET_IS_MANAGED_CAMPAIGN]: (state, getter, rootState, rootGetters) => (state.campaign?.type === 'MANAGED' || state.campaign?.type === 'MANAGED_NETWORK') || (!state.campaign?.type && rootGetters[`account/${accountTypes.GET_IS_MANAGED_ACCOUNT}`]),
  [types.GET_IS_SELF_SERVICE_CAMPAIGN]: (state) => state.campaign?.type === 'SELF_SERVICE',
  [types.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN]: (state, getter, rootState, rootGetters) => rootGetters[`account/${accountTypes.GET_IS_MANAGED_ACCOUNT}`] && getter.GET_IS_SELF_SERVICE_CAMPAIGN && !getter.GET_HAS_CAMPAIGN_ACCESS,
  [types.GET_IS_NEW_CAMPAIGN]: (state) => !state.campaign.hasOwnProperty('id'),
  [types.GET_IS_CAMPAIGN_DRAFT]: (state) => state.campaign?.state === 'DRAFT',
  [types.GET_IS_CAMPAIGN_REVIEW]: (state) => state.campaign?.state === 'REVIEW',
  [types.GET_IS_CAMPAIGN_LIVE]: (state) => state.campaign?.state === 'LIVE',
  [types.GET_IS_CAMPAIGN_DORMANT]: (state) => state.campaign?.state === 'DORMANT',
  [types.GET_IS_CAMPAIGN_PAUSED]: (state) => state.campaign?.state === 'PAUSED',
  [types.GET_IS_CAMPAIGN_ENDED]: (state) => state.campaign?.state === 'ENDED',
  [types.GET_IS_CAMPAIGN_STATE]: (state) => (campaignState) => (state.campaign?.state === campaignState),
  [types.GET_IS_CAMPAIGN_READONLY]: (state, getter) => state.campaign?.id && (getter.GET_IS_CAMPAIGN_ENDED || !getter.GET_HAS_CAMPAIGN_ACCESS || getter.GET_IS_SELF_SERVICE_CAMPAIGN_ACCESSED_BY_ADMIN),
  [types.GET_IS_VIEW_TYPE]: (state) => (viewType) => state.viewType?.toLowerCase() === viewType.toLowerCase(),
  [types.GET_CAMPAIGN_PERMISSIONS]: (state) => state.permissions,
  [types.GET_HAS_OWNER_ACCESS]: (state, getter) => {
    const campaignPermissions = getter.GET_CAMPAIGN_PERMISSIONS.get(state.campaign?.id);
    return campaignPermissions?.ownerAccess;
  },
  [types.GET_HAS_CAMPAIGN_ACCESS]: (state, getter) => {
    const campaignPermissions = getter.GET_CAMPAIGN_PERMISSIONS.get(state.campaign?.id);
    return campaignPermissions?.ownerAccess || campaignPermissions?.sharedTypes.includes('CAMPAIGN');
  },
  [types.GET_HAS_SHOWCASE_ACCESS]: (state, getter) => getter.GET_HAS_CAMPAIGN_ACCESS || getter.GET_CAMPAIGN_PERMISSIONS.get(state.campaign?.id)?.sharedTypes.includes('SHOWCASE'),
  [types.GET_ORDERS_START_DATE]: (state) => {
    if (state.orders.length !== 0) {
      return state.orders.reduce((min, obj) => (obj.startDate < min ? obj.startDate : min), state.orders[0].startDate);
    }
    return state.order.startDate;
  },
  [types.GET_ORDERS_END_DATE]: (state) => {
    if (state.orders.length !== 0) {
      return state.orders.reduce((max, obj) => (obj.endDate > max ? obj.endDate : max), state.orders[0].endDate);
    }
    return state.order.endDate;
  },
  [types.GET_ORDERS_CURRENCY]: (state) => (state.orders.length !== 0 ? state.orders[0].currencyId : state.order.currencyId),
};

export default getters;
